import { useOidc } from "@axa-fr/react-oidc";
import { useQuery } from "graphql-hooks";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row } from 'reactstrap';
import { FleetSummary } from './FleetSummary';
import { useAPIErrorContext } from '../../../contexts/apiErrorContext'
import { Hero } from "../../Shared/Hero";
import { Loading } from '../../Shared/Loading';
import { useFetchWrapper } from "../../../hooks/useFetchWrapper";
import { MetaTags } from "../../Shared/MetaTags";
import { ErrorPage } from "../../Shared/ErrorPage";
import { PermissionsGate } from "../../../utilities/PermissionsGate";
import { ACCESS_VALUES, PageNames } from "../../../utilities/constValues";
import { PermissionsRequired } from "../../Shared/PermissionsRequired";
import { ErrorBanner } from "../../Shared/ErrorBanner";
import { clientConfig } from "../../../utilities/configService";
import { ContentBlock } from "../../Shared/Content/ContentBlock";
import { ContentBody } from "../../Shared/Content/ContentBody";

const CMS_QUERY = `query datoQuery {
    pageData: pageEstimatorMyFleet {
        fieldHeroTitle
        fieldBodyHeader
        fieldBodyContent1
        seo: _seoMetaTags {
            attributes
            content
            tag
        }
    }
}`;

interface IFleet {
    fleetName: string,
    fleetID: string,
    organizationTypeID: number,
    organizationTypeName: string,
    fleetCreatedDate: Date,
    fleetUpdatedDate: Date,
    fleetLastViewed: Date
}

type SortOrder = 'asc' | 'desc';

const sortFleetsBy = (fleets: IFleet[], key: keyof IFleet, direction: SortOrder): IFleet[] => {
    const factor = direction === 'desc' ? -1 : 1;
    return [...fleets].sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return -1 * factor;
        if (bValue == null) return 1 * factor;

        if (aValue < bValue) return -1 * factor;
        if (aValue > bValue) return 1 * factor;
        return 0;
    });
};

export const ViewFleets = () => {
    const fetchWrapper = useFetchWrapper();
    const { addError, clearError, trackError } = useAPIErrorContext();
    const { loading, error, data } = useQuery(CMS_QUERY);
    const applicationName = clientConfig?.APPLICATION_NAME || '';
    const navigate = useNavigate();
    const [fleets, setFleets] = useState<IFleet[]>([]);
    const [currentSortValue, setCurrentSortValue] = useState('');
    const [currentSortDirection, setCurrentSortDirection] = useState('asc');
    // Vehicle count per fleet, reported by each fleet's FleetSummary once it loads.
    const [vehicleCounts, setVehicleCounts] = useState<Record<string, number>>({});

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleDropdown = async () => {
        setDropdownOpen(state => !state);
    }

    useEffect(() => {
        getFleets();
    }, []);


    const addFleet = () => {
        navigate('/CreateFleet');
    }

    const editFleet = (selectedFleetID: any) => {
        navigate(`/EditFleet/${selectedFleetID}`)
    }

    const viewResults = (viewFleetID: any) => {
        navigate(`/ViewEstimate/${viewFleetID}`)
    }

    const duplicateFleet = (viewFleetID: any, fleetName: string) => {
        clearError();
        var duplicateFleetRequest = {
            FleetID: viewFleetID,
            FleetName: "Copy of " + fleetName,
            ApplicationName: applicationName
        };

        fetchWrapper.post('fleet/duplicate', 'application/json', duplicateFleetRequest)
            .then((data) => {
                if (data.success) {
                    getFleets();
                } else {
                    addError("Error duplicating fleet", data.message);
                }
            }).catch((error: any) => {
                addError("Error duplicating fleet", error);
            });
    };

    const deleteFleet = (viewFleetID: any) => {
        if (window.confirm("Are you sure you want to delete this fleet?")) {
            clearError();
            var deleteFleetRequest = {
                FleetID: viewFleetID
            };

            fetchWrapper.post('fleet/delete', 'application/json', deleteFleetRequest)
                .then((data) => {
                    if (data.success) {
                        getFleets();
                    } else {
                        addError("Error deleting fleet", data.message);
                    }
                }).catch((error: any) => {
                    addError("Error deleting fleet", error);
                });
        }
    };

    const getFleets = () => {
        fetchWrapper.get(`fleet/${applicationName}`)
            .then((data) => {
                if (data.fleets) {
                    setFleets(data.fleets);
                }
            });
    }

    const setSorting = (selection: string) => {
        var sortedArray: IFleet[];
        var sortDirection: SortOrder = 'asc';
        if (selection === currentSortValue) {
            if (currentSortDirection === 'asc') {
                sortDirection = 'desc';
            }
            else {
                sortDirection = 'asc';
            }
        }

        if (selection === 'name') {
            sortedArray = sortFleetsBy(fleets, 'fleetName', sortDirection);
        }
        else if (selection === 'dateCreated') {
            sortedArray = sortFleetsBy(fleets, 'fleetCreatedDate', sortDirection);
        }
        else if (selection === 'lastUpdated') {
            sortedArray = sortFleetsBy(fleets, 'fleetUpdatedDate', sortDirection);
        }
        else {
            sortedArray = sortFleetsBy(fleets, selection as keyof IFleet, 'asc');
        }
        setFleets(sortedArray);
        setCurrentSortValue(selection);
        setCurrentSortDirection(sortDirection);
    }

    const setSortingByDropDown = (sortVal: string, sortDir: string) => {
        console.log("sortValue: ", sortVal);
        console.log("sortDirection: ", sortDir);
        var sortedArray: IFleet[];
        var sortDirection: SortOrder = sortDir === 'asc' ? 'asc' : 'desc';

        if (sortVal === 'name') {
            sortedArray = sortFleetsBy(fleets, 'fleetName', sortDirection);
        }
        else if (sortVal === 'dateCreated') {
            sortedArray = sortFleetsBy(fleets, 'fleetCreatedDate', sortDirection);
        }
        else if (sortVal === 'lastUpdated') {
            sortedArray = sortFleetsBy(fleets, 'fleetUpdatedDate', sortDirection);
        }
        else {
            sortedArray = sortFleetsBy(fleets, sortVal as keyof IFleet, 'asc');
        }
        setFleets(sortedArray);
        setCurrentSortValue(sortVal);
        setCurrentSortDirection(sortDirection);
    }

    if (!error && !loading) {
        return (
            <PermissionsGate requestedPermission={ACCESS_VALUES.Resources} renderError={<PermissionsRequired showParticipantMsg={true} />} pageName={PageNames.ViewFleets}>

                <div className="viewFleetsContainer">
                    <Hero pageTitle={data.pageData.fieldHeroTitle} />
                    <ErrorBanner key="createEditFleet" errorCode="form" />
                    <Row>
                        <Col className="pageAnchorsBody">
                            <Row className="pageAnchorsSection desktopView">
                                <Col className="inPageNav" >
                                    <span className="sortByTitle">Sort by:</span>
                                    <Button className={`buttonAsLink sorting ${currentSortValue === 'name' ? 'active' : ''} ${currentSortDirection}`} onClick={e => setSorting("name")}>Name</Button>
                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                    <Button className={`buttonAsLink sorting ${currentSortValue === 'dateCreated' ? 'active' : ''} ${currentSortDirection}`} onClick={e => setSorting("dateCreated")}>Date Created</Button>
                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                    <Button className={`buttonAsLink sorting ${currentSortValue === 'lastUpdated' ? 'active' : ''} ${currentSortDirection}`} onClick={e => setSorting("lastUpdated")}>Last Updated</Button>
                                </Col>
                            </Row>
                            <Row className="pageAnchorsSection mobileView">
                                <Col sm={12} className="inPageNav" >
                                    <Dropdown id="pageAnchorsDropDown" className="pageAnchorsDropDown" direction="down" isOpen={dropdownOpen} toggle={toggleDropdown}>
                                        <DropdownToggle caret>
                                            Sort by...&nbsp;
                                        </DropdownToggle>
                                        <DropdownMenu>
                                            <DropdownItem className={`sortingItem  ${currentSortValue === 'name' && currentSortDirection === 'asc' ? 'active' : ''}`} >
                                                <div onClick={e => setSortingByDropDown("name", "asc")}>Name A-Z</div>
                                            </DropdownItem>
                                            <DropdownItem className={`sortingItem  ${currentSortValue === 'name' && currentSortDirection === 'desc' ? 'active' : ''}`} >
                                                <div onClick={e => setSortingByDropDown("name", "desc")}>Name Z-A</div>
                                            </DropdownItem>
                                            <DropdownItem className={`sortingItem  ${currentSortValue === 'dateCreated' && currentSortDirection === 'asc' ? 'active' : ''}`} >
                                                <div className="dateSortAscending" onClick={e => setSortingByDropDown("dateCreated", "asc")}>Date Created</div>
                                            </DropdownItem>
                                            <DropdownItem className={`sortingItem  ${currentSortValue === 'dateCreated' && currentSortDirection === 'desc' ? 'active' : ''}`} >
                                                <div className="dateSortDescending" onClick={e => setSortingByDropDown("dateCreated", "desc")}>Date Created</div>
                                            </DropdownItem>
                                            <DropdownItem className={`sortingItem  ${currentSortValue === 'lastUpdated' && currentSortDirection === 'asc' ? 'active' : ''}`} >
                                                <div className="dateSortAscending" onClick={e => setSortingByDropDown("lastUpdated", "asc")}>Last Updated</div>
                                            </DropdownItem>
                                            <DropdownItem className={`sortingItem  ${currentSortValue === 'lastUpdated' && currentSortDirection === 'desc' ? 'active' : ''}`} >
                                                <div className="dateSortDescending" onClick={e => setSortingByDropDown("lastUpdated", "desc")}>Last Updated</div>
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </Col>
                            </Row>

                        </Col>
                    </Row>
                    <ContentBlock className="viewFleetsContentBlock">
                        <Row className="headingRow">
                            <Col className="sectionPadding contentBlock">
                                <Button name="AddFleet" className="submitButton solidNeg primaryButton iconPlusSign addFleetButton" onClick={addFleet}>Add A Fleet</Button>
                            </Col>
                        </Row>
                        {fleets ? (
                            <ContentBody>
                                {fleets.map((item: IFleet, index: number) => {
                                    return (
                                        <Row key={index} className="fleetData">
                                            <Col>
                                                <Row className="contentBlock fleetTitleBlock fleetTitleRow">
                                                    <Col lg={8} md={8} sm={12}>
                                                        <h2 className="css_heading bold">{item.fleetName}</h2>
                                                        <div className="body">{item.organizationTypeName}</div>
                                                    </Col>
                                                    <Col lg={4} md={4} sm={12} className="viewEstimateButtonCol">
                                                        <Button name="ViewEstimate" className="submitButton solidNegGreen primaryButton iconRightArrow" onClick={() => { viewResults(item.fleetID) }} disabled={!(vehicleCounts[item.fleetID] > 0)}>View Estimate</Button>
                                                    </Col>
                                                </Row>
                                                <Row className="fleetActionRow">
                                                    <Col>
                                                        <Button name="ViewEstimate" className="submitButton solidPos secondaryButton iconRightArrowBlue editButton" onClick={() => { editFleet(item.fleetID) }}>Edit</Button>
                                                        <Button name="ViewEstimate" className="submitButton hollowNeg secondaryButton iconRightArrow dupButton" onClick={() => { duplicateFleet(item.fleetID, item.fleetName) }}>Duplicate</Button>
                                                        <Button name="ViewEstimate" className="submitButton solidPos secondaryButton iconTrashBlue delButton" onClick={() => { deleteFleet(item.fleetID) }}>Delete</Button>
                                                    </Col>
                                                </Row>
                                                <FleetSummary fleetID={item.fleetID} onVehicleCountLoaded={count => setVehicleCounts(counts => ({ ...counts, [item.fleetID]: count }))} />
                                            </Col>

                                        </Row>
                                    )
                                })
                                }
                            </ContentBody>
                        ) : (<></>)}
                    </ContentBlock>

                    {/* set meta tags */}
                    <MetaTags seoData={data.pageData.seo} />

                </div>
            </PermissionsGate>
        );
    }
    else if (!error) {
        return (<Loading showContent={true} includeHero={true} />);
    }
    else {
        trackError("Dato error", JSON.stringify(error));
        return (<ErrorPage showContactForm={true} includeHero={true} />);
    }
};
